import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  ClockIcon,
  UserGroupIcon,
  FolderIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';



interface DayTasksModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  tasks: any[];
  onTaskClick: (task: any) => void;
  selectedMember?: string;
  users?: any[];
}

const DayTasksModal: React.FC<DayTasksModalProps> = ({
  isOpen,
  onClose,
  date,
  tasks,
  onTaskClick,
  selectedMember,
  users
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DONE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'TODO':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-500';
      case 'MEDIUM':
        return 'bg-yellow-500';
      case 'LOW':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getMemberName = () => {
    if (!selectedMember || selectedMember === 'all') return 'All Members';
    const user = users?.find(u => u.id === selectedMember);
    return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-opacity-75"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <CalendarIcon className="w-6 h-6 text-primary-600" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Tasks for {formatDate(date)}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {selectedMember && selectedMember !== 'all' ? `${getMemberName()} • ` : ''}
                      {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
                {tasks.length === 0 ? (
                  <div className="text-center py-8">
                    <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No tasks for this day</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tasks.map((task, index) => (
                      <motion.div
                        key={task.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.05 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onTaskClick(task);
                        }}
                        className={`p-4 rounded-lg border cursor-pointer hover:shadow-md transition-all ${getStatusColor(task.status)}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <div className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`} />
                              <h4 className="font-medium text-gray-900">{task.title}</h4>
                              {task.type === 'recurring' && (
                                <ArrowPathIcon className="w-4 h-4 text-blue-500" title="Recurring Task" />
                              )}
                              {task.isRecurringInstance && task.type !== 'recurring' && (
                                <ArrowPathIcon className="w-4 h-4 text-green-500" title="Generated from Recurring Task" />
                              )}
                              {task.type === 'due' && (
                                <ClockIcon className="w-4 h-4 text-gray-500" title="Due Date" />
                              )}
                              {task.dueDate && isOverdue(task.dueDate) && task.status !== 'DONE' && (
                                <ExclamationTriangleIcon className="w-4 h-4 text-red-500" title="Overdue" />
                              )}
                            </div>
                            
                            {task.description && (
                              <p className="text-sm text-gray-600 mb-2 line-clamp-2">{task.description}</p>
                            )}
                            
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              {task.team && (
                                <div className="flex items-center space-x-1">
                                  <UserGroupIcon className="w-3 h-3" />
                                  <span>{task.team.name}</span>
                                </div>
                              )}
                              {task.project && (
                                <div className="flex items-center space-x-1">
                                  <FolderIcon className="w-3 h-3" />
                                  <span>{task.project.name}</span>
                                </div>
                              )}
                              {task.assignees && task.assignees.length > 0 && (
                                <div className="flex items-center space-x-1">
                                  <span>Assigned to: {task.assignees.map((a: any) => a.user?.firstName || a.firstName).join(', ')}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="ml-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                              {task.status.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

            {/* Footer */}
            <div className="flex justify-end p-6 border-t border-gray-200">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Close
                </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default DayTasksModal;
